import { MagnifyingGlass, WarningCircle, X } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { useToast } from '@/hooks/use-toast';
import { useInfiniteSearchContacts } from '@/modules/contacts/hooks/use-contacts';
import { getContactDisplayName } from '@/modules/contacts/utils/contact-attributes';
import { useAddListMembers } from '../hooks/use-lists';

const PAGE_SIZE = 10;

export type AddListMembersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
  listName: string;
  existingMemberIds: string[];
};

export const AddListMembersModal = ({
  open,
  onOpenChange,
  listId,
  listName,
  existingMemberIds,
}: AddListMembersModalProps) => {
  const { toast } = useToast();
  const { mutateAsync: addMembers, isPending } = useAddListMembers();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteSearchContacts(search, { pageSize: PAGE_SIZE, enabled: open });

  const memberIdSet = useMemo(() => new Set(existingMemberIds), [existingMemberIds]);

  const candidates = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((page) => page.items)
        .filter((contact) => contact.ItemId && !memberIdSet.has(contact.ItemId)),
    [data?.pages, memberIdSet]
  );

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedIds([]);
  }, [open]);

  useEffect(() => {
    if (!open || isLoading || isFetchingNextPage || !hasNextPage) return;
    if (candidates.length > 0) return;
    void fetchNextPage();
  }, [open, isLoading, isFetchingNextPage, hasNextPage, candidates.length, fetchNextPage]);

  useEffect(() => {
    const root = listRef.current;
    const target = loadMoreRef.current;
    if (!open || !root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (!hasNextPage || isFetchingNextPage) return;
        void fetchNextPage();
      },
      { root, rootMargin: '80px', threshold: 0 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [open, fetchNextPage, hasNextPage, isFetchingNextPage, candidates.length]);

  const toggleSelected = (contactId: string) => {
    setSelectedIds((current) =>
      current.includes(contactId)
        ? current.filter((id) => id !== contactId)
        : [...current, contactId]
    );
  };

  const handleAdd = async () => {
    if (selectedIds.length === 0) return;

    try {
      await addMembers({ listId, contactIds: selectedIds });
      toast({
        variant: 'success',
        title: `${selectedIds.length} contact${selectedIds.length === 1 ? '' : 's'} added to ${listName}.`,
      });
      onOpenChange(false);
    } catch (addError) {
      toast({
        variant: 'destructive',
        title: 'Failed to add members',
        description: addError instanceof Error ? addError.message : 'Please try again.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        overlayClassName="bg-black/40"
        className="flex max-h-[80vh] w-full max-w-[520px] flex-col gap-0 overflow-hidden rounded-lg border-neutral-200 p-0 shadow-e4 sm:max-w-[520px]"
      >
        <DialogHeader className="flex h-14 flex-none flex-row items-center justify-between space-y-0 border-b border-neutral-200 px-5">
          <DialogTitle className="text-base font-semibold leading-6 text-neutral-900">
            Add members
          </DialogTitle>
          <DialogClose className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800">
            <X className="size-[18px]" aria-hidden />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="flex-none border-b border-neutral-200 px-5 py-4">
          <div className="flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-2 focus-within:border-primary-600 focus-within:ring-2 focus-within:ring-primary-600/30">
            <MagnifyingGlass className="size-4 shrink-0 text-neutral-500" aria-hidden />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search contacts by name, email, or phone"
              className="h-auto border-0 p-0 shadow-none focus-visible:ring-0"
              aria-label="Search contacts by name, email, or phone"
            />
          </div>
          <p className="mt-1.5 text-xs text-neutral-600">Showing contacts not already on this list.</p>
        </div>

        <div ref={listRef} className="min-h-[180px] flex-1 overflow-y-auto px-2 py-2">
          {isLoading ? (
            <p className="px-3 py-8 text-center text-sm text-neutral-500">Loading contacts...</p>
          ) : error ? (
            <div className="flex items-start gap-2 px-3 py-8 text-sm text-error">
              <WarningCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              {error instanceof Error ? error.message : 'Failed to load contacts'}
            </div>
          ) : candidates.length === 0 && !hasNextPage && !isFetchingNextPage ? (
            <p className="px-3 py-8 text-center text-sm text-neutral-500">
              {search.trim() ? 'No matching contacts to add.' : 'No contacts available to add.'}
            </p>
          ) : candidates.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-neutral-500">Loading contacts...</p>
          ) : (
            <>
              {candidates.map((contact) => {
                const checked = selectedIds.includes(contact.ItemId);
                const displayName = getContactDisplayName(contact) || contact.Email || contact.ItemId;

                return (
                  <label
                    key={contact.ItemId}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-neutral-50"
                  >
                    <Checkbox
                      checked={checked}
                      className="size-[18px] rounded-[4px] border-neutral-300 data-[state=checked]:border-primary-600 data-[state=checked]:bg-primary-600"
                      onCheckedChange={() => toggleSelected(contact.ItemId)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-neutral-900">{displayName}</span>
                      {contact.Email ? (
                        <span className="text-neutral-500"> · {contact.Email}</span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
              <div ref={loadMoreRef} className="h-4 w-full" aria-hidden />
              {isFetchingNextPage ? (
                <p className="px-3 py-3 text-center text-xs text-neutral-500">Loading more…</p>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter className="flex-none flex-row items-center justify-between gap-3 border-t border-neutral-200 px-5 py-4 sm:justify-between">
          <span className="text-[13px] text-neutral-600">
            {selectedIds.length} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              className="h-10 px-4 text-neutral-800 hover:bg-neutral-100"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
              disabled={selectedIds.length === 0 || isPending}
              loading={isPending}
              onClick={() => void handleAdd()}
            >
              Add
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
